import React, { KeyboardEvent, useState } from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as yup from 'yup';
import * as XLSX from 'xlsx';

import { CrossIcon } from '../../../../assets/svg/SVGComponents';
import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';
import Input from '../../../../components/Input';
import Select from '../../../../components/Selects/SimpleSelect';
import { FormCard, StepTitle } from '../styles';
import {
  AddMediaButton,
  AlunosRow,
  BatchActionsRow,
  ChipRemoveButton,
  ChipsRow,
  ClearChipButton,
  DownloadLink,
  FieldGroup,
  FieldHint,
  FieldLabel,
  FieldLabelRow,
  FormRow,
  SecondaryRowButton,
  SaveRowButton,
  SelectedChipBox,
  SelectedChipText,
  StudentChip,
  StudentInputArrow,
  Tab,
  TabRow,
  TeamsTable,
} from './StepTeams.styles';
import {
  CATEGORY_NUMBER_TO_LABEL,
  ITournamentSetupData,
  MODALITY_SLUG_TO_NAME,
} from '..';

interface ITeamRow {
  team: string;
  modality: string;
  category: string;
  members: string[];
}

interface ITeamMemberInput {
  studentName: string;
  schoolYear: number;
}

interface IBatchTeamRow {
  id: string;
  teamName: string;
  modalityInput: string;
  categoryInput: string;
  membersInput: string;
  fieldErrors: {
    teamName?: string;
    modalityInput?: string;
    categoryInput?: string;
    membersInput?: string;
  };
}

interface IStepTeamsProps {
  tournamentData: ITournamentSetupData;
  hideBatchImport?: boolean;
  onSaveSuccess?: () => void;
}

const MIN_STUDENTS = 3;
const MAX_STUDENTS = 5;

const MODALITY_ALLOWED_CATEGORIES: Record<string, number[]> = {
  robo_pintor: [1, 2],
  seguidor_de_linha: [3, 4],
};

const normalizeText = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const TEAM_HEADERS = [
  'name',
  'team',
  'equipe',
  'nome equipe',
  'nome da equipe',
  'nome_da_equipe',
];

const MODALITY_HEADERS = [
  'modality',
  'modality slug',
  'modalidade',
  'modalidade slug',
  'modalidade da equipe',
  'modalidade (robo_pintor ou seguidor_de_linha)',
  'modality_id',
];

const CATEGORY_HEADERS = [
  'category',
  'categoria',
  'categoria da equipe',
  'categoria (1 a 4)',
  'category_label',
];

const MEMBER_NAME_HEADERS = [
  'student_name',
  'student name',
  'aluno',
  'nome do aluno',
  'integrante',
  'membro',
];

const SCHOOL_YEAR_HEADERS = [
  'school_year',
  'school year',
  'ano',
  'ano escolar',
  'serie',
  'serie escolar',
  'série',
];

const MEMBERS_HEADERS = [
  'members',
  'integrantes',
  'lista integrantes',
  'lista de integrantes',
];

const IGNORED_BATCH_TEAM_NAMES = new Set([
  normalizeText('Equipe exemplo 1'),
  normalizeText('Equipe exemplo 2'),
]);

const isIgnoredBatchTeamName = (teamName: string): boolean =>
  IGNORED_BATCH_TEAM_NAMES.has(normalizeText(teamName));

const memberSchema = yup.object().shape({
  studentName: yup.string().trim().required('Informe o nome do aluno.'),
  schoolYear: yup
    .number()
    .typeError('Informe um ano escolar válido.')
    .integer('O ano escolar deve ser inteiro.')
    .min(1, 'O ano escolar deve ser maior ou igual a 1.')
    .required('Informe o ano escolar.'),
});

const normalizeNullableString = (value: unknown) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const extractIndexedMembersFromRow = (
  normalizedRow: Record<string, string>,
) => {
  const memberNameByIndex: Record<number, string> = {};
  const memberYearByIndex: Record<number, string> = {};

  Object.entries(normalizedRow).forEach(([key, value]) => {
    const nameMatch = key.match(
      /^(nome do aluno|aluno|integrante|nome)\s*(\d+)$/,
    );
    if (nameMatch) {
      memberNameByIndex[Number(nameMatch[2])] = value;
      return;
    }

    const yearMatch = key.match(/^(ano escolar|ano|serie|série)\s*(\d+)$/);
    if (yearMatch) {
      memberYearByIndex[Number(yearMatch[2])] = value;
    }
  });

  const indices = Array.from(
    new Set([
      ...Object.keys(memberNameByIndex).map(Number),
      ...Object.keys(memberYearByIndex).map(Number),
    ]),
  ).sort((a, b) => a - b);

  return indices
    .map(index => ({
      name: memberNameByIndex[index] ?? '',
      year: memberYearByIndex[index] ?? '',
    }))
    .filter(member => member.name || member.year);
};

const findMatchingHeaderKey = (
  rowKeys: string[],
  aliases: string[],
): string | null => {
  const normalizedAliases = aliases.map(alias => normalizeText(alias));

  const exactMatch = rowKeys.find(rowKey => normalizedAliases.includes(rowKey));
  if (exactMatch) return exactMatch;

  const containsAliasMatch = rowKeys.find(rowKey =>
    normalizedAliases.some(alias => rowKey.includes(alias)),
  );
  if (containsAliasMatch) return containsAliasMatch;

  const aliasContainsKeyMatch = rowKeys.find(rowKey =>
    normalizedAliases.some(alias => alias.includes(rowKey)),
  );
  return aliasContainsKeyMatch ?? null;
};

const StepTeams: React.FC<IStepTeamsProps> = ({
  tournamentData,
  hideBatchImport = false,
  onSaveSuccess,
}) => {
  const formRef = React.useRef<FormHandles>(null);
  const batchFileInputRef = React.useRef<HTMLInputElement>(null);
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<'manual' | 'batch'>(
    hideBatchImport ? 'manual' : 'manual',
  );

  const [teamName, setTeamName] = useState('');
  const [selectedModalitySlug, setSelectedModalitySlug] = useState<
    string | null
  >(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [studentNameInput, setStudentNameInput] = useState('');
  const [schoolYearInput, setSchoolYearInput] = useState('');
  const [teamMembers, setTeamMembers] = useState<ITeamMemberInput[]>([]);
  const [savedTeams, setSavedTeams] = useState<ITeamRow[]>([]);
  const [isSavingTeam, setIsSavingTeam] = useState(false);
  const [isSavingBatchTeams, setIsSavingBatchTeams] = useState(false);
  const [membersError, setMembersError] = useState('');
  const [batchRows, setBatchRows] = useState<IBatchTeamRow[]>([]);
  const [batchFileName, setBatchFileName] = useState('');

  const modalityOptions = Object.entries(MODALITY_SLUG_TO_NAME).map(
    ([value, label]) => ({ value, label }),
  );

  const categoryOptions = Object.entries(CATEGORY_NUMBER_TO_LABEL).map(
    ([value, label]) => ({ value: `category_${value}`, label }),
  );

  const filteredCategoryOptions = selectedModalitySlug
    ? categoryOptions.filter(opt => {
        const num = parseInt(opt.value.replace('category_', ''), 10);
        return (
          MODALITY_ALLOWED_CATEGORIES[selectedModalitySlug] ?? []
        ).includes(num);
      })
    : categoryOptions;

  const modalityIdBySlug = tournamentData.modalities.reduce(
    (acc, modalityItem) => {
      acc[modalityItem.modality] = modalityItem.id;
      return acc;
    },
    {} as Record<string, string>,
  );

  const availableModalities = tournamentData.arenas.length
    ? [...new Set(tournamentData.arenas.map(a => a.modality))].map(slug => ({
        value: slug,
        label: MODALITY_SLUG_TO_NAME[slug] ?? slug,
      }))
    : modalityOptions;

  const selectedModalityId = selectedModalitySlug
    ? (modalityIdBySlug[selectedModalitySlug] ?? null)
    : null;

  // Atualizado para receber a linha já normalizada e otimizar as buscas
  const getSpreadsheetCell = (
    normalizedRow: Record<string, string>,
    aliases: string[],
  ): string => {
    const key = findMatchingHeaderKey(Object.keys(normalizedRow), aliases);
    return key ? normalizedRow[key] : '';
  };

  const resolveCategoryValue = (rawValue: string): string | null => {
    const normalizedValue = normalizeText(rawValue);
    if (!normalizedValue) return null;

    if (/^category_[1-4]$/.test(normalizedValue)) {
      return normalizedValue;
    }

    if (/^[1-4]$/.test(normalizedValue)) {
      return `category_${normalizedValue}`;
    }

    const matchedOption = categoryOptions.find(
      option => normalizeText(option.label) === normalizedValue,
    );
    return matchedOption?.value ?? null;
  };

  const resolveModalitySlug = (rawValue: string): string | null => {
    const normalizedValue = normalizeText(rawValue);
    if (!normalizedValue) return null;

    const directSlug = availableModalities.find(
      option => normalizeText(option.value) === normalizedValue,
    );
    if (directSlug) return directSlug.value;

    const byLabel = availableModalities.find(
      option => normalizeText(option.label) === normalizedValue,
    );

    return byLabel?.value ?? null;
  };

  const parseMembersInput = (rawMembers: string) => {
    const chunks = rawMembers
      .split(/;|\n/g)
      .map(chunk => chunk.trim())
      .filter(Boolean);

    const members: ITeamMemberInput[] = [];
    const parseErrors: string[] = [];

    chunks.forEach(chunk => {
      const byPipe = chunk.split('|').map(part => part.trim());
      const byDash = chunk.split('-').map(part => part.trim());
      const [nameFromPipe, yearFromPipe] = byPipe;
      const [nameFromDash, yearFromDash] = byDash;
      const hasPipeFormat = byPipe.length >= 2;
      const hasDashFormat = byDash.length >= 2;

      const studentName = hasPipeFormat
        ? nameFromPipe
        : hasDashFormat
          ? nameFromDash
          : chunk; // Resiliência: se colocar só o nome sem o ano, captura o nome

      const schoolYearRaw = hasPipeFormat
        ? yearFromPipe
        : hasDashFormat
          ? yearFromDash
          : '';
      const schoolYear = Number(schoolYearRaw);

      if (!studentName || !schoolYearRaw) {
        parseErrors.push(`Integrante "${chunk}" deve usar formato Nome|Ano.`);
        return;
      }

      members.push({ studentName, schoolYear });
    });

    return { members, parseErrors };
  };

  const getBatchRowFieldErrors = (row: IBatchTeamRow) => {
    const resolvedCategory = resolveCategoryValue(row.categoryInput);
    const resolvedModalitySlug = resolveModalitySlug(row.modalityInput);
    const resolvedModalityId = resolvedModalitySlug
      ? (modalityIdBySlug[resolvedModalitySlug] ?? null)
      : null;

    const { members, parseErrors } = parseMembersInput(row.membersInput);
    const fieldErrors: IBatchTeamRow['fieldErrors'] = {};

    if (parseErrors.length > 0) {
      fieldErrors.membersInput = parseErrors.join(' | ');
    }

    try {
      teamSchema.validateSync(
        {
          teamName: row.teamName,
          modalityId: resolvedModalityId,
          category: resolvedCategory,
          members,
        },
        { abortEarly: false },
      );
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const issues = error.inner.length ? error.inner : [error];
        issues.forEach(issue => {
          const rawMessage = issue.message ?? '';
          const normalizedMessage =
            issue.path === 'modalityId' &&
            rawMessage.includes('must be a `string` type')
              ? 'Selecione uma modalidade.'
              : issue.path === 'category' &&
                  rawMessage.includes('must be a `string` type')
                ? 'Selecione uma categoria.'
                : rawMessage;

          if (!normalizedMessage) {
            return;
          }

          if (issue.path === 'teamName') {
            fieldErrors.teamName = normalizedMessage;
            return;
          }

          if (issue.path === 'modalityId') {
            fieldErrors.modalityInput = normalizedMessage;
            return;
          }

          if (issue.path === 'category') {
            fieldErrors.categoryInput = normalizedMessage;
            return;
          }

          if (
            issue.path === 'members' ||
            issue.path?.startsWith('members[') ||
            issue.path?.startsWith('members.')
          ) {
            fieldErrors.membersInput = fieldErrors.membersInput
              ? `${fieldErrors.membersInput} | ${normalizedMessage}`
              : normalizedMessage;
          }
        });
      }
    }

    return fieldErrors;
  };

  const validateBatchRow = (row: IBatchTeamRow): IBatchTeamRow => ({
    ...row,
    fieldErrors: getBatchRowFieldErrors(row),
  });

  const parseBatchFile = (worksheetRows: Record<string, unknown>[]) => {
    const groupedRows = new Map<
      string,
      {
        teamName: string;
        modalityInput: string;
        categoryInput: string;
        membersInput: string;
        membersInputSet: Set<string>;
      }
    >();

    let lastTeamName = '';
    let lastModalityInput = '';
    let lastCategoryInput = '';

    worksheetRows.forEach(rawRow => {
      // Normalizamos as chaves da linha do Excel uma única vez
      const normalizedRow = Object.entries(rawRow).reduce(
        (acc, [key, value]) => {
          acc[normalizeText(key)] = String(value ?? '').trim();
          return acc;
        },
        {} as Record<string, string>,
      );

      const rawRowTeamName = getSpreadsheetCell(normalizedRow, TEAM_HEADERS);
      let rowTeamName = rawRowTeamName;
      let modalityInput = getSpreadsheetCell(normalizedRow, MODALITY_HEADERS);
      let categoryInput = getSpreadsheetCell(normalizedRow, CATEGORY_HEADERS);

      // "Fill Down": útil se o usuário mesclar as células de Equipe/Modalidade no Excel
      if (rawRowTeamName) {
        if (isIgnoredBatchTeamName(rawRowTeamName)) {
          return;
        }
        lastTeamName = rawRowTeamName;
      } else {
        rowTeamName = lastTeamName;
      }

      if (modalityInput) lastModalityInput = modalityInput;
      else modalityInput = lastModalityInput;

      if (categoryInput) lastCategoryInput = categoryInput;
      else categoryInput = lastCategoryInput;

      if (!rowTeamName && !modalityInput && !categoryInput) {
        return;
      }

      const groupKey = `${rowTeamName}|${modalityInput}|${categoryInput}`;
      const current = groupedRows.get(groupKey) ?? {
        teamName: rowTeamName,
        modalityInput,
        categoryInput,
        membersInput: '',
        membersInputSet: new Set<string>(),
      };

      // 1. Abordagem: Única coluna de integrantes (Ex: Joao|9; Maria|8)
      const membersInput = getSpreadsheetCell(normalizedRow, MEMBERS_HEADERS);
      if (membersInput) {
        membersInput
          .split(/;|\n/g)
          .map(chunk => chunk.trim())
          .filter(Boolean)
          .forEach(chunk => current.membersInputSet.add(chunk));
      }

      // 2. Abordagem: Um aluno por linha da planilha (Aluno, Ano)
      const memberName = getSpreadsheetCell(normalizedRow, MEMBER_NAME_HEADERS);
      const schoolYear = getSpreadsheetCell(normalizedRow, SCHOOL_YEAR_HEADERS);
      if (memberName || schoolYear) {
        current.membersInputSet.add(`${memberName}|${schoolYear}`);
      }

      // 3. Abordagem: Múltiplas colunas para alunos na mesma linha (ex: Nome do Aluno 1 / Ano Escolar 1 / ...)
      const indexedMembers = extractIndexedMembersFromRow(normalizedRow);
      indexedMembers.forEach(member => {
        current.membersInputSet.add(`${member.name}|${member.year}`);
      });

      current.membersInput = Array.from(current.membersInputSet).join(';\n');
      groupedRows.set(groupKey, current);
    });

    const parsedRows = Array.from(groupedRows.values()).map(group =>
      validateBatchRow({
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        teamName: group.teamName,
        modalityInput: group.modalityInput,
        categoryInput: group.categoryInput,
        membersInput: group.membersInput,
        fieldErrors: {},
      }),
    );

    return parsedRows;
  };

  const handleBatchRowChange = (
    rowId: string,
    field: 'teamName' | 'modalityInput' | 'categoryInput' | 'membersInput',
    value: string,
  ) => {
    setBatchRows(prevRows =>
      prevRows.map(row => {
        if (row.id !== rowId) return row;
        return validateBatchRow({ ...row, [field]: value });
      }),
    );
  };

  const handleBatchFileUploadClick = () => {
    batchFileInputRef.current?.click();
  };

  const handleCancelBatchImport = () => {
    setBatchRows([]);
    setBatchFileName('');

    addToast({
      type: 'success',
      expression: 'Sucesso!',
      description: 'Importação em lote cancelada.',
    });
  };

  const handleBatchFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const [file] = Array.from(event.target.files ?? []);
    event.target.value = '';

    if (!file) {
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const worksheetRows = XLSX.utils.sheet_to_json(worksheet, {
        defval: '',
        raw: false,
      }) as Record<string, unknown>[];

      const parsedRows = parseBatchFile(worksheetRows);
      setBatchRows(parsedRows);
      setBatchFileName(file.name);

      if (!parsedRows.length) {
        addToast({
          type: 'error',
          expression: 'Erro!',
          description: 'A planilha não possui linhas válidas para importar.',
        });
        return;
      }

      const invalidRows = parsedRows.filter(
        row => Object.keys(row.fieldErrors).length > 0,
      ).length;
      addToast({
        type: invalidRows > 0 ? 'error' : 'success',
        expression: invalidRows > 0 ? 'Atenção!' : 'Sucesso!',
        description:
          invalidRows > 0
            ? `${invalidRows} equipe(s) com erro. Corrija na tabela abaixo.`
            : 'Planilha lida com sucesso.',
      });
    } catch {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: 'Não foi possível interpretar o arquivo de importação.',
      });
    }
  };

  const handleSaveBatchTeams = async () => {
    if (!tournamentData.id) {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: 'Salve os dados do torneio antes de cadastrar equipes.',
      });
      return;
    }

    const validatedRows = batchRows.map(validateBatchRow);
    setBatchRows(validatedRows);

    const validRows = validatedRows.filter(
      row => Object.keys(row.fieldErrors).length === 0,
    );

    if (!validRows.length) {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: 'Nenhuma linha válida para salvar.',
      });
      return;
    }

    const payload = validRows
      .map(row => {
        const resolvedModalitySlug = resolveModalitySlug(row.modalityInput);
        const resolvedModalityId = resolvedModalitySlug
          ? (modalityIdBySlug[resolvedModalitySlug] ?? null)
          : null;
        const resolvedCategory = resolveCategoryValue(row.categoryInput);
        const { members } = parseMembersInput(row.membersInput);

        if (!resolvedModalityId || !resolvedCategory || !members.length) {
          return null;
        }

        return {
          name: row.teamName.trim(),
          modality_id: resolvedModalityId,
          category: resolvedCategory,
          members: members.map(member => ({
            student_name: member.studentName,
            school_year: member.schoolYear,
          })),
        };
      })
      .filter(Boolean) as {
      name: string;
      modality_id: string;
      category: string;
      members: { student_name: string; school_year: number }[];
    }[];

    if (!payload.length) {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: 'Nenhuma linha válida para salvar.',
      });
      return;
    }

    try {
      setIsSavingBatchTeams(true);
      await api.post(`/tournaments/${tournamentData.id}/teams`, payload);

      const rowsByName = new Map(
        validRows.map(row => [row.teamName.trim(), row]),
      );
      const savedBatchTeams: ITeamRow[] = payload.map(team => {
        const sourceRow = rowsByName.get(team.name);
        const sourceModalitySlug = sourceRow
          ? resolveModalitySlug(sourceRow.modalityInput)
          : null;

        return {
          team: team.name,
          modality: sourceModalitySlug
            ? (MODALITY_SLUG_TO_NAME[sourceModalitySlug] ?? sourceModalitySlug)
            : '-',
          category: getCategoryLabel(team.category),
          members: team.members.map(
            member => `${member.student_name} (${member.school_year}º ano)`,
          ),
        };
      });

      setSavedTeams(prev => [...prev, ...savedBatchTeams]);

      const invalidRows = validatedRows.filter(
        row => Object.keys(row.fieldErrors).length > 0,
      );
      setBatchRows(invalidRows);
      if (!invalidRows.length) {
        setBatchFileName('');
      }

      addToast({
        type: 'success',
        expression: 'Sucesso!',
        description:
          invalidRows.length > 0
            ? `${payload.length} equipe(s) salvas. Corrija as linhas restantes para salvar o restante.`
            : `${payload.length} equipe(s) salvas com sucesso.`,
      });

      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: 'Não foi possível salvar as equipes em lote.',
      });
    } finally {
      setIsSavingBatchTeams(false);
    }
  };

  const teamSchema = yup.object().shape({
    teamName: yup.string().trim().required('Informe o nome da equipe.'),
    modalityId: yup
      .mixed<string | null>()
      .nullable()
      .transform((value, originalValue) =>
        normalizeNullableString(originalValue ?? value),
      )
      .required('Selecione uma modalidade.')
      .test('valid-modality-id', 'Selecione uma modalidade válida.', value => {
        if (!value) return true;
        return yup.string().uuid().isValidSync(value);
      }),
    category: yup
      .mixed<string | null>()
      .nullable()
      .transform((value, originalValue) =>
        normalizeNullableString(originalValue ?? value),
      )
      .required('Selecione uma categoria.')
      .test('valid-category', 'Selecione uma categoria válida.', value => {
        if (!value) return true;
        return categoryOptions.some(option => option.value === value);
      }),
    members: yup
      .array()
      .of(memberSchema)
      .min(MIN_STUDENTS, `Adicione no mínimo ${MIN_STUDENTS} integrantes.`)
      .required(),
  });

  const handleAddMember = () => {
    const trimmedName = studentNameInput.trim();
    const parsedYear = Number(schoolYearInput);
    const hasDuplicate = teamMembers.some(
      member =>
        member.studentName.toLowerCase() === trimmedName.toLowerCase() &&
        member.schoolYear === parsedYear,
    );

    if (hasDuplicate) {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: 'Esse integrante já foi adicionado.',
      });
      return;
    }

    try {
      memberSchema.validateSync({
        studentName: trimmedName,
        schoolYear: parsedYear,
      });
      setTeamMembers(prev => [
        ...prev,
        { studentName: trimmedName, schoolYear: parsedYear },
      ]);
      setStudentNameInput('');
      setSchoolYearInput('');
      setMembersError('');
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        addToast({
          type: 'error',
          expression: 'Erro!',
          description: error.message,
        });
      }
    }
  };

  const handleMemberInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddMember();
    }
  };

  const handleRemoveMember = (memberToRemove: ITeamMemberInput) => {
    setTeamMembers(prev =>
      prev.filter(
        member =>
          !(
            member.studentName === memberToRemove.studentName &&
            member.schoolYear === memberToRemove.schoolYear
          ),
      ),
    );
  };

  const canAddMember = teamMembers.length < MAX_STUDENTS;

  const canSave =
    teamName.trim() !== '' &&
    Boolean(selectedModalitySlug) &&
    Boolean(selectedModalityId) &&
    Boolean(selectedCategory) &&
    teamMembers.length >= MIN_STUDENTS;

  const getCategoryLabel = (categoryValue: string) => {
    const option = categoryOptions.find(item => item.value === categoryValue);
    return option?.label ?? categoryValue;
  };

  const handleSaveTeam = async () => {
    if (!tournamentData.id) {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: 'Salve os dados do torneio antes de cadastrar equipes.',
      });
      return;
    }

    try {
      setIsSavingTeam(true);
      formRef.current?.setErrors({});
      setMembersError('');

      await teamSchema.validate(
        {
          teamName: teamName.trim(),
          modalityId: selectedModalityId,
          category: selectedCategory,
          members: teamMembers,
        },
        { abortEarly: false },
      );

      if (!selectedModalityId || !selectedCategory) {
        return;
      }

      const payload = {
        name: teamName.trim(),
        modality_id: selectedModalityId,
        category: selectedCategory,
        members: teamMembers.map(member => ({
          student_name: member.studentName,
          school_year: member.schoolYear,
        })),
      };

      await api.post(`/tournaments/${tournamentData.id}/teams`, payload);

      setSavedTeams(prev => [
        ...prev,
        {
          team: teamName.trim(),
          modality:
            MODALITY_SLUG_TO_NAME[selectedModalitySlug] ?? selectedModalitySlug,
          category: getCategoryLabel(selectedCategory),
          members: teamMembers.map(
            member => `${member.studentName} (${member.schoolYear}º ano)`,
          ),
        },
      ]);

      setTeamName('');
      setSelectedModalitySlug(null);
      setSelectedCategory(null);
      setTeamMembers([]);
      setStudentNameInput('');
      setSchoolYearInput('');

      addToast({
        type: 'success',
        expression: 'Sucesso!',
        description: 'Equipe salva com sucesso.',
      });

      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const fieldErrors: Record<string, string> = {};
        const issues = error.inner.length ? error.inner : [error];

        issues.forEach(issue => {
          if (issue.path === 'teamName') {
            fieldErrors.team_name = issue.message;
          }
          if (issue.path === 'modalityId') {
            fieldErrors.modality_id = issue.message;
          }
          if (issue.path === 'category') {
            fieldErrors.category = issue.message;
          }
          if (issue.path === 'members') {
            setMembersError(issue.message);
          }
        });

        formRef.current?.setErrors(fieldErrors);
      } else {
        addToast({
          type: 'error',
          expression: 'Erro!',
          description: 'Não foi possível salvar a equipe. Tente novamente.',
        });
      }
    } finally {
      setIsSavingTeam(false);
    }
  };

  return (
    <FormCard>
      <StepTitle>Etapa 4: Equipes</StepTitle>

      {!hideBatchImport && (
        <TabRow>
          <Tab
            type="button"
            $active={activeTab === 'manual'}
            onClick={() => setActiveTab('manual')}
          >
            Adicionar manualmente
          </Tab>
          <Tab
            type="button"
            $active={activeTab === 'batch'}
            onClick={() => setActiveTab('batch')}
          >
            Importação em lote
          </Tab>
        </TabRow>
      )}

      {!hideBatchImport && activeTab === 'batch' && (
        <>
          <BatchActionsRow>
            <DownloadLink
              href="https://fuzzyplaydev.s3.us-east-2.amazonaws.com/Modelo-de-Importacao-de-equipes.xlsx"
              download
            >
              Baixar planilha de template
            </DownloadLink>
            <AddMediaButton type="button" onClick={handleBatchFileUploadClick}>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="3" y1="15" x2="21" y2="15" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </svg>
              </span>
              Importar planilha
            </AddMediaButton>
            <input
              ref={batchFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleBatchFileChange}
              style={{ display: 'none' }}
            />
          </BatchActionsRow>

          {batchFileName && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.8rem',
                marginBottom: '0.5rem',
              }}
            >
              <FieldHint
                style={{ color: '#555' }}
              >{`Arquivo: ${batchFileName}`}</FieldHint>
              <SecondaryRowButton
                type="button"
                onClick={handleCancelBatchImport}
                disabled={isSavingBatchTeams}
              >
                Cancelar importação
              </SecondaryRowButton>
            </div>
          )}

          {batchRows.length > 0 && (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginBottom: '0.5rem',
                }}
              >
                <SaveRowButton
                  type="button"
                  onClick={handleSaveBatchTeams}
                  disabled={!batchRows.length || isSavingBatchTeams}
                >
                  {isSavingBatchTeams ? 'Salvando...' : 'Salvar em lote'}
                </SaveRowButton>
              </div>

              <TeamsTable>
                <colgroup>
                  <col style={{ width: '30%' }} />
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '38%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ minWidth: '12rem' }}>Equipe</th>
                    <th style={{ minWidth: '10rem' }}>Modalidade</th>
                    <th style={{ minWidth: '1rem' }}>Categoria</th>
                    <th style={{ minWidth: '8rem' }}>Integrantes (Nome|Ano)</th>
                  </tr>
                </thead>
                <tbody>
                  {batchRows.map(row => (
                    <tr
                      key={row.id}
                      style={
                        Object.keys(row.fieldErrors).length > 0
                          ? { background: '#ffebee' }
                          : undefined
                      }
                    >
                      <td>
                        <input
                          value={row.teamName}
                          onChange={e =>
                            handleBatchRowChange(
                              row.id,
                              'teamName',
                              e.target.value,
                            )
                          }
                          style={{ width: '100%' }}
                        />
                        {row.fieldErrors.teamName && (
                          <div
                            style={{ color: '#c62828', fontSize: '0.75rem' }}
                          >
                            {row.fieldErrors.teamName}
                          </div>
                        )}
                      </td>
                      <td>
                        <input
                          value={row.modalityInput}
                          onChange={e =>
                            handleBatchRowChange(
                              row.id,
                              'modalityInput',
                              e.target.value,
                            )
                          }
                          style={{ width: '100%' }}
                        />
                        {row.fieldErrors.modalityInput && (
                          <div
                            style={{ color: '#c62828', fontSize: '0.75rem' }}
                          >
                            {row.fieldErrors.modalityInput}
                          </div>
                        )}
                      </td>
                      <td>
                        <input
                          value={row.categoryInput}
                          onChange={e =>
                            handleBatchRowChange(
                              row.id,
                              'categoryInput',
                              e.target.value,
                            )
                          }
                          style={{ width: '100%' }}
                        />
                        {row.fieldErrors.categoryInput && (
                          <div
                            style={{ color: '#c62828', fontSize: '0.75rem' }}
                          >
                            {row.fieldErrors.categoryInput}
                          </div>
                        )}
                      </td>
                      <td>
                        <textarea
                          value={row.membersInput}
                          onChange={e =>
                            handleBatchRowChange(
                              row.id,
                              'membersInput',
                              e.target.value,
                            )
                          }
                          rows={3}
                          style={{ width: '100%' }}
                        />
                        {row.fieldErrors.membersInput && (
                          <div
                            style={{ color: '#c62828', fontSize: '0.75rem' }}
                          >
                            {row.fieldErrors.membersInput}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TeamsTable>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: '0.5rem',
                }}
              >
                <SaveRowButton
                  type="button"
                  onClick={handleSaveBatchTeams}
                  disabled={!batchRows.length || isSavingBatchTeams}
                >
                  {isSavingBatchTeams ? 'Salvando...' : 'Salvar em lote'}
                </SaveRowButton>
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'manual' && (
        <Form
          ref={formRef}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onSubmit={() => {}}
        >
          <FormRow>
            <FieldGroup $flex={2}>
              <FieldLabelRow>
                <FieldLabel>Nome da Equipe</FieldLabel>
              </FieldLabelRow>
              <Input
                name="team_name"
                placeholder="Ex: BotMakers"
                value={teamName}
                onChange={e => setTeamName(e.target.value)}
              />
            </FieldGroup>

            <FieldGroup $flex={1.5}>
              <FieldLabel>Modalidade da Equipe:</FieldLabel>
              {selectedModalitySlug ? (
                <SelectedChipBox>
                  <SelectedChipText>
                    {MODALITY_SLUG_TO_NAME[selectedModalitySlug] ??
                      selectedModalitySlug}
                  </SelectedChipText>
                  <ClearChipButton
                    type="button"
                    onClick={() => setSelectedModalitySlug(null)}
                    aria-label="Remover modalidade"
                  >
                    <CrossIcon
                      width="0.75rem"
                      stroke="currentColor"
                      fill="none"
                      strokeWidth={2.5}
                    />
                  </ClearChipButton>
                </SelectedChipBox>
              ) : (
                <Select
                  name="modality_id"
                  options={availableModalities}
                  value={
                    selectedModalitySlug
                      ? (availableModalities.find(
                          option => option.value === selectedModalitySlug,
                        ) ?? null)
                      : null
                  }
                  onChange={(
                    option: { value: string; label: string } | null,
                  ) => {
                    setSelectedModalitySlug(option?.value ?? null);
                    setSelectedCategory(null);
                  }}
                  isClearable
                  placeholder="Selecionar..."
                  styles={{
                    control: (base: object) => ({
                      ...base,
                      height: '3.05rem',
                      minHeight: '3.05rem',
                      borderRadius: '0.56rem',
                    }),
                  }}
                />
              )}
            </FieldGroup>

            <FieldGroup $flex={1.2}>
              <FieldLabel>Categoria:</FieldLabel>
              {selectedCategory ? (
                <SelectedChipBox>
                  <SelectedChipText>
                    {getCategoryLabel(selectedCategory)}
                  </SelectedChipText>
                  <ClearChipButton
                    type="button"
                    onClick={() => setSelectedCategory(null)}
                    aria-label="Remover categoria"
                  >
                    <CrossIcon
                      width="0.75rem"
                      stroke="currentColor"
                      fill="none"
                      strokeWidth={2.5}
                    />
                  </ClearChipButton>
                </SelectedChipBox>
              ) : (
                <Select
                  name="category"
                  options={filteredCategoryOptions}
                  value={
                    selectedCategory
                      ? (filteredCategoryOptions.find(
                          option => option.value === selectedCategory,
                        ) ?? null)
                      : null
                  }
                  onChange={(option: { value: string; label: string } | null) =>
                    setSelectedCategory(option?.value ?? null)
                  }
                  isClearable
                  placeholder="Selecionar..."
                  styles={{
                    control: (base: object) => ({
                      ...base,
                      height: '3.05rem',
                      minHeight: '3.05rem',
                      borderRadius: '0.56rem',
                    }),
                  }}
                />
              )}
            </FieldGroup>
          </FormRow>

          <AlunosRow>
            <FieldLabelRow style={{ minWidth: '100%' }}>
              <FieldLabel>Alunos:</FieldLabel>
              <FieldHint>{`${teamMembers.length}/${MAX_STUDENTS} integrantes (mín. ${MIN_STUDENTS})`}</FieldHint>
            </FieldLabelRow>

            <FormRow>
              <FieldGroup $flex={2}>
                <Input
                  name="member_name"
                  placeholder="Digite o nome"
                  value={studentNameInput}
                  onChange={e => setStudentNameInput(e.target.value)}
                  onKeyDown={handleMemberInputKeyDown}
                  disabled={!canAddMember}
                />
              </FieldGroup>
              <FieldGroup $flex={1}>
                <Input
                  name="member_school_year"
                  placeholder="Ano escolar"
                  type="number"
                  min={1}
                  value={schoolYearInput}
                  onChange={e => setSchoolYearInput(e.target.value)}
                  onKeyDown={handleMemberInputKeyDown}
                  disabled={!canAddMember}
                />
              </FieldGroup>
              <StudentInputArrow
                type="button"
                onClick={handleAddMember}
                disabled={!canAddMember}
              >
                <svg
                  width="1.5rem"
                  height="1.5rem"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1e88e1"
                  strokeWidth={2.5}
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </StudentInputArrow>
            </FormRow>

            {membersError && <FieldHint>{membersError}</FieldHint>}

            <ChipsRow>
              {teamMembers.map(member => {
                const memberLabel = `${member.studentName} (${member.schoolYear}º ano)`;
                return (
                  <StudentChip
                    key={`${member.studentName}-${member.schoolYear}`}
                  >
                    {memberLabel}
                    <ChipRemoveButton
                      type="button"
                      onClick={() => handleRemoveMember(member)}
                      aria-label={`Remover ${memberLabel}`}
                    >
                      <CrossIcon
                        width="0.7rem"
                        stroke="currentColor"
                        fill="none"
                        strokeWidth={2.5}
                      />
                    </ChipRemoveButton>
                  </StudentChip>
                );
              })}
            </ChipsRow>
          </AlunosRow>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '0.5rem',
            }}
          >
            <SaveRowButton
              type="button"
              onClick={handleSaveTeam}
              disabled={!canSave || isSavingTeam}
            >
              {isSavingTeam ? 'Salvando...' : '+ Salvar'}
            </SaveRowButton>
          </div>
        </Form>
      )}

      {savedTeams.length > 0 && (
        <TeamsTable>
          <thead>
            <tr>
              <th>Equipe</th>
              <th>Modalidade</th>
              <th>Categoria</th>
              <th>Integrantes</th>
            </tr>
          </thead>
          <tbody>
            {savedTeams.map((team, idx) => (
              <tr key={idx}>
                <td>{team.team}</td>
                <td>{team.modality}</td>
                <td>{team.category}</td>
                <td>{team.members.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </TeamsTable>
      )}
    </FormCard>
  );
};

export { StepTeams };
